import { createContext, useCallback, useContext, useEffect, useState } from "react"
import { Routes, Route, Navigate, useParams, Link, useLocation } from "react-router-dom"

import { APIError, useAuth } from "../../../../app/modules/auth"
import { FullCircleOrganization, getFCAOrganizationByUniqueIdentifier } from "../../../core/_requests"
import MogulHeader from "../../../components/MogulHeader"
import FCAOrgSettings from "./FCAOrgSettings"

type FullCircleOrganizationContextProps = {
    fcaOrganization: FullCircleOrganization
}

const FullCircleOrganizationContext = createContext<FullCircleOrganizationContextProps>({
    fcaOrganization: {} as FullCircleOrganization,
})

export const useFullCircleOrganizationDetail = () => {
    return useContext(FullCircleOrganizationContext)
}

function FCAManageOrgDetail() {

    const { organization_unique_identifier: fcaOrganizationUniqueIdentifier } = useParams<{
        organization_unique_identifier: string
    }>()

    // const {
    //     currentUser
    // } = useAuth()

    const { pathname } = useLocation();

    const [fcaOrganization, setFCAOrganization] = useState<FullCircleOrganization | null>(null);
    const [loadingServiceLocations, setLoadingServiceLocations] = useState(true);
    const [errorLoadingServiceLocations, setErrorLoadingServiceLocations] = useState<string | null>(null);

    // const [viewingTab] = useState<'events' | 'events_administrators' | 'events_check_in_records' | 'settings'>('events')

    const loadServiceLocation = useCallback(async () => {
        setErrorLoadingServiceLocations(null)
        setLoadingServiceLocations(false)
        if (!fcaOrganizationUniqueIdentifier) {
            setErrorLoadingServiceLocations('No FCA organization unique identifier provided.')
            return
        }
        setLoadingServiceLocations(true)
        setErrorLoadingServiceLocations(null)
        try {
            const updatedOrg = await getFCAOrganizationByUniqueIdentifier(fcaOrganizationUniqueIdentifier)
            setFCAOrganization(updatedOrg)
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'Failed to load FCA organization.'
            setErrorLoadingServiceLocations(errorMessage)
        } finally {
            setLoadingServiceLocations(false)
        }
    }, [fcaOrganizationUniqueIdentifier])

    useEffect(() => {
        loadServiceLocation()
    }, [loadServiceLocation])

    if (loadingServiceLocations) {
        return <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
        </div>
    }

    if (errorLoadingServiceLocations || !fcaOrganization) {
        return <div>
            <div className="alert alert-danger" role="alert">
                {errorLoadingServiceLocations || 'Failed to load FCA organization.'}
            </div>
            <div className='mt-5'>
                <button className='btn btn-primary btn-lg' onClick={loadServiceLocation}>
                    Retry
                </button>
            </div>
        </div>
    }

    return <FullCircleOrganizationContext.Provider value={{
        fcaOrganization,
    }}>
        <MogulHeader title={`${fcaOrganization.org_name}`} />
        {/*}
        <PageTitle breadcrumbs={[
            {
                title: 'Home',
                path: '/dashboard',
                isSeparator: false,
                isActive: false,
            },
        ]}>
            {`Service Location: ${serviceLocation.name}`}
        </PageTitle>
        <div>
            {`Service Location Identifier: ${serviceLocation.unique_identifier}`}
        </div>
        */}
        <div className="position-relative">
            {/* One day we will have a better design, but for now relative is important to get the lead download button correctly positioned */}
            <div className="header-menu align-items-stretch my-5">
                <div className="menu menu-lg-rounded menu-column menu-lg-row menu-state-bg menu-title-gray-700 menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary menu-arrow-gray-400 fw-bold my-5 my-lg-0 align-items-stretch">
                    <div className="menu-item me-lg-1">
                        <Link
                            to={`/fca/manage-org/${fcaOrganizationUniqueIdentifier}/settings`}
                            className={["menu-link py-3 btn btn-link-primary", pathname === `/fca/manage-org/${fcaOrganizationUniqueIdentifier}/settings` ? "active" : ""].join(" ")}
                        >
                            <span className="menu-title">Settings</span>
                        </Link>
                    </div>
                </div>
            </div>
            <Routes>
                <Route path='settings' element={<FCAOrgSettings />} />
                <Route path='*' element={<Navigate to={`/fca/manage-org/${fcaOrganizationUniqueIdentifier}/settings`} replace />} />
            </Routes>
        </div>
    </FullCircleOrganizationContext.Provider >

}

export default FCAManageOrgDetail